//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { theme } from '@bvengineering/unison-core';
import { SeriesAreaOptions } from 'highcharts';

import { TikTokGender } from '@apis/tiktok-user/tiktokUserApiModel';
import { DAYS_OF_WEEK } from '@utils/moment';

export const tiktokAudienceStatBubbleTitles = {
  changeInFollowers: 'Change in Followers',
  profileViews: 'Profile Views',
};

const OVER_THE_SELECTED_TIME_PERIOD = ' over the selected time period.';

export const tiktokAudienceStatBubbleDefinitionsBase = {
  changeInFollowers: 'Net increase or decrease in followers',
  profileViews: 'Number of profile views',
};

export const tiktokAudienceStatBubbleTooltips = {
  changeInFollowers: `${tiktokAudienceStatBubbleDefinitionsBase.changeInFollowers}${OVER_THE_SELECTED_TIME_PERIOD}`,
  profileViews: `${tiktokAudienceStatBubbleDefinitionsBase.profileViews}${OVER_THE_SELECTED_TIME_PERIOD}`,
};

export const TikTokAudienceChartTooltips = {
  AUDIENCE_GENDER: 'Gender distribution of followers over the last 60 days.',
  AUDIENCE_COUNTRIES:
    'Country distribution of followers over the last 60 days.',
};

export const TikTokAudienceBreakdownTitles = {
  AUDIENCE_GENDER: 'Followers by Gender',
  AUDIENCE_COUNTRIES: 'Followers by Country',
};

export const TIKTOK_AUDIENCE_SUBTITLE = 'Last 60 Days';

export const FOLLOWERS_BY_LOCATION_LIMIT = 10;
export const FOLLOWER_DEMOGRAPHIC_CHART_WIDTH = 304;

export const BASE_CHART_HEIGHT = 350;
export const BASE_CHART_WIDTH = 900;

export const BASE_CHART_OPTIONS: Highcharts.Options = {
  chart: {
    height: BASE_CHART_HEIGHT,
    width: BASE_CHART_WIDTH,
    backgroundColor: theme.colors.white,
    borderColor: theme.colors.white,
  },
  series: [
    {
      turboThreshold: 0,
      color: theme.colors.blue2,
    },
  ] as SeriesAreaOptions[],
};

export const AUDIENCE_EMPTY_DAILY_METRICS = {
  changeInFollowers: 0,
  followersCount: 0,
  profileViews: 0,
  videoViews: 0,
  likeCount: 0,
  commentCount: 0,
  shareCount: 0,
  audienceActivity: [],
};

export const getGenderColor = (gender: TikTokGender) => {
  switch (gender) {
    case TikTokGender.Male:
      return theme.colors.blue3;
    case TikTokGender.Female:
      return theme.colors.blue2;
    case TikTokGender.Other:
      return theme.colors.blue1;
  }
};

const { cyan0, cyan9, mint1, mint2, mint3, mint5 } = theme.colors;

export const tiktokOnlineFollowersColors = [
  cyan0,
  mint1,
  mint3,
  mint5,
  cyan9,
] as const;

export const tiktokRankedTimesColors = [cyan9, mint5, mint2] as const;

export const HOUR_HEADER_CELL_WIDTH = 6;

export const heatmapCellWidth = `${
  (100 - HOUR_HEADER_CELL_WIDTH) / DAYS_OF_WEEK.length
}%`;

export const TIKTOK_HEATMAP_TITLE = 'When Your Followers are Online';

export const TIKTOK_HEATMAP_TOOLTIP =
  'This heatmap illustrates the number of followers online for each hour of the week based on your average TikTok account data from the past 4 weeks. Each day is uniquely calculated. Hover over individual cells to see more detailed information.';

export const TIKTOK_NOT_ENOUGH_FOLLOWERS_TOOLTIP =
  'Not enough online follower data available.';
